/**
 * PildoresIdEstadistiques.tsx
 * Admin-only view: per-student progress for a Conjunt de Pildores.
 * Styled like ActivitatsIdEstadistiques: one avatar card per student with a row
 * of colored squares (one per pildora): green = completed, orange = in progress,
 * red = attempted but failed, grey = not started. Clicking a square opens a modal
 * with that student's submissions for that pildora (code, test output, statements,
 * unit tests).
 */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../../../../config/AuthContext';
import { GetConjuntPildoresEstadistiques } from '../../../../dao/jutge/GetConjuntPildoresEstadistiques';
import { GetRankingsUsuariId } from '../../../../dao/jutge/GetRankingsUsuariId';
import { GetAlumneId } from '../../../../dao/GetAlumneId';
import { DefaultAulumneInfo } from '../../../../model/AlumneInfo';
import PildoraAlumneDetailModal from './PildoraAlumneDetailModal';
import {
    Row, Col, Card, Typography, Statistic, Tag, Avatar, Tooltip,
    Button, message, Empty, Spin, Input, Space, Modal
} from 'antd';
import {
    TrophyOutlined, CheckCircleOutlined,
    UserOutlined, ReloadOutlined, SearchOutlined, GiftOutlined, UserSwitchOutlined
} from '@ant-design/icons';

const { Text } = Typography;

interface PildoresIdEstadistiquesProps {
    id_conjunt?: string | number;
}

// ── Normalise a backend student row into a stable shape ───────────────────────
interface PildoraCell {
    id_caza: number;
    nombre: string;
    dificultat?: string;
    totalPasos: number;
    max_paso_completado: number;
    intentos: number;
    obtained: number;
    instrucciones?: string;
    tiempo_empleado_segundos?: number;
    reward_type?: string;
    reward_valor?: number;
    item_id?: number;
    id_javamon?: number;
    javamon_nom?: string;
    rewards?: Array<{
        id: number;
        tipo: string;
        valor?: number;
        id_item?: string | number;
        imagen?: string | null;
    }>;
}
interface StudentRow {
    id_usuari: string;
    nom: string;
    cognoms: string;
    nom_usuari: string;
    avatar?: string;
    total_pildores: number;
    pildores_completades: number;
    recompenses_obtingudes: number;
    pildores: PildoraCell[];
}

const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
};

const getAvatarSrc = (img?: string | null) => {
    if (!img || img === 'undefined' || img === 'null') return undefined;
    if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('/')) return img;
    if (img.startsWith('base64:')) img = img.split(':').pop()!;
    if (img.startsWith('/9j/')) return `data:image/jpeg;base64,${img}`;
    return `data:image/png;base64,${img}`;
};

// Colour of a pildora square for a student
const cellColor = (c: PildoraCell): string => {
    const total = c.totalPasos || 0;
    const done = c.max_paso_completado || 0;
    const completed = c.obtained === 1 || (total > 0 && done >= total);
    if (completed) return '#52c41a';          // green: completed
    if (done > 0) return '#faad14';            // orange: in progress
    if ((c.intentos || 0) > 0) return '#f5222d'; // red: attempted, failed
    return '#f0f0f0';                          // grey: not started
};

const PildoresIdEstadistiques: React.FC<PildoresIdEstadistiquesProps> = (props) => {
    const routeParams = useParams<{ id_conjunt: string }>();
    const id_conjunt = props.id_conjunt !== undefined ? String(props.id_conjunt) : routeParams.id_conjunt;
    const { isLoggedIn, user, userId, setLoggedIn, setUserId, setUser, setAlumneInfo, setUserPermisos } = useContext(AuthContext);
    const canDelete = user && (user.tipus === '0' || user.tipus === '1');
    const isAdmin = canDelete;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';

    const [dades, setDades] = useState<StudentRow[]>([]);
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Rewards modal
    const [rewardsModalStudent, setRewardsModalStudent] = useState<StudentRow | null>(null);

    // Detail modal (one student + one pildora)
    const [detail, setDetail] = useState<{ idCaza: string; idUsuari: string; pildoraTitle: string; studentName: string; totalSteps: number; instrucciones?: string; tiempoEmpleadoSegundos?: number } | null>(null);

    const fetchData = async () => {
        if (!id_conjunt) return;
        setLoading(true);
        try {
            const [data, rankData] = await Promise.all([
                GetConjuntPildoresEstadistiques(id_conjunt),
                GetRankingsUsuariId(String(userId)).catch(() => null)
            ]);

            // Build id_usuari -> avatar map from the ranking (same approach as ActivitatsIdEstadistiques)
            if (rankData) {
                const ranking = Array.isArray(rankData) ? rankData : (rankData.ranking || []);
                const map: Record<string, string> = {};
                ranking.forEach((r: any) => {
                    const uid = r.id_usuari || r.id;
                    if (uid && r.avatar) map[String(uid)] = r.avatar;
                });
                setAvatars(map);
            }

            const rows: StudentRow[] = (Array.isArray(data) ? data : []).map((d: any) => ({
                id_usuari: String(d.id_usuari ?? d.id ?? ''),
                nom: d.nom ?? '',
                cognoms: d.cognoms ?? '',
                nom_usuari: d.nom_usuari ?? d.usuari ?? d.email ?? '',
                avatar: d.avatar,
                total_pildores: Number(d.total_pildores ?? (Array.isArray(d.pildores) ? d.pildores.length : 0)),
                pildores_completades: Number(d.pildores_completades ?? 0),
                recompenses_obtingudes: Number(d.recompenses_obtingudes ?? 0),
                pildores: (Array.isArray(d.pildores) ? d.pildores : []).map((p: any) => ({
                    id_caza: Number(p.id_caza ?? p.id),
                    nombre: p.nombre ?? p.nom ?? `Píldora ${p.id_caza ?? p.id}`,
                    dificultat: p.dificultat ?? p.dificultad,
                    totalPasos: Number(p.totalPasos ?? p.total_pasos ?? 0),
                    max_paso_completado: Number(p.max_paso_completado ?? 0),
                    intentos: Number(p.intentos ?? 0),
                    obtained: Number(p.obtained ?? 0),
                    instrucciones: p.instrucciones ?? p.instruccions,
                    tiempo_empleado_segundos: p.tiempo_empleado_segundos ?? p.tiempo,
                    reward_type: p.reward_type ?? p.rewardType,
                    reward_valor: p.reward_valor != null ? Number(p.reward_valor) : undefined,
                    item_id: p.item_id ?? p.itemId,
                    id_javamon: p.id_javamon != null ? Number(p.id_javamon) : undefined,
                    javamon_nom: p.javamon_nom,
                    rewards: Array.isArray(p.rewards) ? p.rewards : undefined,
                })),
            }));
            setDades(rows);
        } catch (e) {
            message.error('Error al carregar les estadístiques');
        }
        setLoading(false);
    };

    useEffect(() => {
        if (isLoggedIn && id_conjunt) fetchData();
    }, [isLoggedIn, id_conjunt]);

    const filtered = useMemo(() => {
        if (!searchText.trim()) return dades;
        const q = searchText.toLowerCase();
        return dades.filter(s =>
            `${s.nom} ${s.cognoms}`.toLowerCase().includes(q) ||
            (s.nom_usuari && s.nom_usuari.toLowerCase().includes(q))
        );
    }, [dades, searchText]);

    const globalStats = useMemo(() => {
        if (!dades.length) return { totalStudents: 0, avgPct: 0, fullCompleted: 0, totalRewards: 0 };
        let sumPct = 0, full = 0, rewards = 0;
        dades.forEach(d => {
            const pct = d.total_pildores > 0 ? Math.round((d.pildores_completades / d.total_pildores) * 100) : 0;
            sumPct += pct;
            if (pct >= 100 && d.total_pildores > 0) full++;
            rewards += d.recompenses_obtingudes || 0;
        });
        return { totalStudents: dades.length, avgPct: Math.round(sumPct / dades.length), fullCompleted: full, totalRewards: rewards };
    }, [dades]);

    const openDetail = (student: StudentRow, cell: PildoraCell) => {
        setDetail({
            idCaza: String(cell.id_caza),
            idUsuari: student.id_usuari,
            pildoraTitle: cell.nombre,
            studentName: `${student.cognoms}, ${student.nom}`.trim().replace(/^,\s*/, '') || student.nom_usuari,
            totalSteps: cell.totalPasos || 1,
            instrucciones: cell.instrucciones,
            tiempoEmpleadoSegundos: cell.tiempo_empleado_segundos,
        });
    };

    // Impersonate a student (same flow as ActivitatsIdEstadistiques)
    const handleImpersonate = async (targetId: string) => {
        try {
            message.loading({ content: 'Impersonant usuari...', key: 'imp' });
            const response = await fetch(`${baseUrl}:8061/login/impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ adminId: userId, targetId })
            });
            if (response.ok) {
                const data = await response.json();
                const { token, permisos } = data;
                let alu = DefaultAulumneInfo;
                if (permisos && permisos.length > 0) {
                    const res = await GetAlumneId(permisos[0].id, token);
                    if (res) alu = res;
                }
                if (!localStorage.getItem('adminToken')) {
                    localStorage.setItem('adminToken', localStorage.getItem('authToken') || '');
                    localStorage.setItem('admin_userId', localStorage.getItem('userId') || '');
                    localStorage.setItem('admin_user', localStorage.getItem('user') || '');
                    localStorage.setItem('admin_userPermisos', localStorage.getItem('userPermisos') || '');
                    localStorage.setItem('admin_alumneInfo', localStorage.getItem('alumneInfo') || '');
                }
                localStorage.setItem('authToken', token);
                setLoggedIn && setLoggedIn(true);
                setUserId && setUserId(String(permisos[0].id));
                setUser && setUser(permisos[0]);
                setUserPermisos && setUserPermisos(permisos);
                setAlumneInfo && setAlumneInfo(alu);
                message.success({ content: 'Impersonant...', key: 'imp' });
                window.location.reload();
            } else {
                message.error({ content: 'Error al impersonar', key: 'imp' });
            }
        } catch (e) {
            message.error({ content: 'Error de connexió', key: 'imp' });
        }
    };

    return (
        <div style={{ padding: '16px 0' }}>
            {/* Header (title is already shown by the parent Modal / sider) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <Input
                    placeholder="Cercar alumne..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 220 }}
                />
                <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Actualitzar</Button>
            </div>

            {/* Global stats */}
            <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col xs={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Statistic title="Alumnes" value={globalStats.totalStudents} prefix={<UserOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Statistic title="Progrés Mig" value={globalStats.avgPct} suffix="%" valueStyle={{ color: '#1890ff' }} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Statistic title="Han completat tot" value={globalStats.fullCompleted} prefix={<TrophyOutlined />} valueStyle={{ color: '#52c41a' }} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <Statistic title="Recompenses" value={globalStats.totalRewards} prefix={<GiftOutlined />} valueStyle={{ color: '#fa8c16' }} />
                    </Card>
                </Col>
            </Row>

            {/* Legend */}
            <Space size={12} style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 12, height: 12, background: '#52c41a', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Completada</span>
                <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 12, height: 12, background: '#faad14', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />En procés</span>
                <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 12, height: 12, background: '#f5222d', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Fallada</span>
                <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 12, height: 12, background: '#f0f0f0', border: '1px solid #e0e0e0', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Sense començar</span>
            </Space>

            {/* Student cards */}
            <Spin spinning={loading}>
                {filtered.length === 0 ? (
                    <Empty description="Cap alumne ha interaccionat encara" style={{ marginTop: 50 }} />
                ) : (
                    <Row gutter={[16, 16]}>
                        {filtered.map(student => {
                            const name = `${student.nom} ${student.cognoms}`.trim() || student.nom_usuari || 'Anònim';
                            const initials = (name || 'NA').substring(0, 2).toUpperCase();
                            const pct = student.total_pildores > 0
                                ? Math.round((student.pildores_completades / student.total_pildores) * 100)
                                : 0;
                            return (
                                <Col xs={24} sm={12} md={8} lg={6} xl={4} key={student.id_usuari}>
                                    <Card
                                        hoverable
                                        bordered={false}
                                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', height: '100%' }}
                                        bodyStyle={{ padding: 12 }}
                                    >
                                        {/* Header: avatar + name + pct */}
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                            {(() => {
                                                const avatarImg = avatars[student.id_usuari] || student.avatar;
                                                return avatarImg ? (
                                                    <div style={{ width: 46, height: 46, borderRadius: '50%', overflow: 'hidden', border: '1px solid #f0f0f0', flexShrink: 0 }}>
                                                        <img src={getAvatarSrc(avatarImg)} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                    </div>
                                                ) : (
                                                    <Avatar size={46} style={{ backgroundColor: stringToColor(name), flexShrink: 0 }}>{initials}</Avatar>
                                                );
                                            })()}
                                            <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0 }}>
                                                <Text strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 14, lineHeight: 1.2 }} title={name}>
                                                    {name}
                                                </Text>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                                                    <Tag color={pct >= 100 ? 'green' : pct > 0 ? 'blue' : 'default'} style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px' }}>
                                                        {pct}%
                                                    </Tag>
                                                    <Tag style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px' }}>
                                                        <CheckCircleOutlined /> {student.pildores_completades}/{student.total_pildores}
                                                    </Tag>
                                                    <Tooltip title="Veure recompenses obtingudes">
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            icon={<GiftOutlined style={{ color: '#faad14', fontSize: 13 }} />}
                                                            style={{ padding: '0 4px', height: 20, minWidth: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                            onClick={(e) => { e.stopPropagation(); setRewardsModalStudent(student); }}
                                                        />
                                                    </Tooltip>
                                                    {isAdmin && (
                                                        <Tooltip title="Impersonar alumne">
                                                            <Button
                                                                size="small"
                                                                type="text"
                                                                icon={<UserSwitchOutlined style={{ color: '#1890ff', fontSize: 13 }} />}
                                                                style={{ padding: '0 4px', height: 20, minWidth: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                onClick={(e) => { e.stopPropagation(); handleImpersonate(student.id_usuari); }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Colored pildora squares */}
                                        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 8 }}>
                                            {student.pildores.length > 0 ? student.pildores.map((cell, idx) => {
                                                const total = cell.totalPasos || 0;
                                                const done = cell.max_paso_completado || 0;
                                                const completed = cell.obtained === 1 || (total > 0 && done >= total);
                                                const stateLabel = completed ? 'Completada'
                                                    : done > 0 ? `En procés (${done}/${total})`
                                                        : (cell.intentos > 0 ? 'Fallada' : 'Sense començar');
                                                return (
                                                    <Tooltip key={cell.id_caza || idx} title={`${cell.nombre} · ${stateLabel} · Intents: ${cell.intentos || 0}`}>
                                                        <div
                                                            onClick={() => openDetail(student, cell)}
                                                            style={{
                                                                width: 24, height: 18, borderRadius: 3,
                                                                backgroundColor: cellColor(cell),
                                                                border: '1px solid #e0e0e0',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 9, fontWeight: 'bold',
                                                                color: (completed || done > 0 || cell.intentos > 0) ? '#fff' : '#8c8c8c',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            {idx + 1}
                                                        </div>
                                                    </Tooltip>
                                                );
                                            }) : (
                                                <Text type="secondary" style={{ fontSize: 10, width: '100%', textAlign: 'center' }}>Sense píldores</Text>
                                            )}
                                        </div>

                                        {/* ID tag */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                            {student.nom_usuari && (
                                                <Text type="secondary" style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={student.nom_usuari}>
                                                    {student.nom_usuari}
                                                </Text>
                                            )}
                                            <Tag style={{ margin: 0, fontSize: 9, lineHeight: '14px', padding: '0 4px', borderRadius: 3, color: '#bfbfbf', borderColor: '#f0f0f0', backgroundColor: '#fafafa' }}>
                                                #{student.id_usuari}
                                            </Tag>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Spin>

            {/* Per student + pildora detail */}
            {detail && (
                <PildoraAlumneDetailModal
                    visible={!!detail}
                    onClose={() => setDetail(null)}
                    idCaza={detail.idCaza}
                    idUsuari={detail.idUsuari}
                    pildoraTitle={detail.pildoraTitle}
                    studentName={detail.studentName}
                    totalSteps={detail.totalSteps}
                    instrucciones={detail.instrucciones}
                    tiempoEmpleadoSegundos={detail.tiempoEmpleadoSegundos}
                    idConjunt={id_conjunt ? parseInt(id_conjunt) : null}
                    canDelete={!!canDelete}
                />
            )}

            {/* Rewards obtained by a student across the conjunt */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <GiftOutlined style={{ color: '#faad14', fontSize: 20 }} />
                        <span>
                            Recompenses de {rewardsModalStudent
                                ? (`${rewardsModalStudent.nom} ${rewardsModalStudent.cognoms}`.trim() || rewardsModalStudent.nom_usuari)
                                : ''}
                        </span>
                    </div>
                }
                open={!!rewardsModalStudent}
                onCancel={() => setRewardsModalStudent(null)}
                footer={[<Button key="close" type="primary" onClick={() => setRewardsModalStudent(null)}>Tancar</Button>]}
                width={560}
            >
                {(() => {
                    // Extract all actual rewards obtained by this student
                    const rewardItems: Array<{
                        key: string;
                        visual: React.ReactNode;
                        label: string;
                        pildoraName: string;
                    }> = [];

                    (rewardsModalStudent?.pildores || []).forEach(p => {
                        if (Array.isArray(p.rewards) && p.rewards.length > 0) {
                            p.rewards.forEach(r => {
                                const type = (r.tipo || 'PUNTUACION').toUpperCase();
                                let visual: React.ReactNode;
                                let label: string;

                                if (type === 'PUNTUACION') {
                                    visual = <span style={{ fontSize: 28 }}>🪙</span>;
                                    label = `+${r.valor ?? 0} Punts`;
                                } else if (type === 'JAVAMON' || type === 'JAVAMON_RANDOM' || type === 'JAVAMON_SEASON') {
                                    const jId = r.id_item || p.id_javamon;
                                    const imgUrl = jId ? `${baseUrl}:8061/jutge/game/image/javamon/${jId}` : null;
                                    visual = imgUrl
                                        ? <img src={imgUrl} alt="javamon" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                                        : <span style={{ fontSize: 28 }}>⚔️</span>;
                                    label = p.javamon_nom || 'Javamon';
                                } else {
                                    let typeSegment = type.toLowerCase();
                                    if (typeSegment === 'escudo') typeSegment = 'escut';
                                    if (typeSegment === 'arco') typeSegment = 'arc';
                                    if (typeSegment === 'shop_item') typeSegment = 'shop';
                                    const imgUrl = r.id_item
                                        ? `${baseUrl}:8061/jutge/game/image/${typeSegment}/${r.id_item}`
                                        : (r.imagen ? `data:image/png;base64,${r.imagen}` : null);
                                    visual = imgUrl
                                        ? <img src={imgUrl} alt={type} style={{ width: 48, height: 48, objectFit: 'contain' }} />
                                        : <span style={{ fontSize: 28 }}>🎁</span>;
                                    label = type;
                                }

                                rewardItems.push({
                                    key: `caza-${p.id_caza}-rew-${r.id}`,
                                    visual,
                                    label,
                                    pildoraName: p.nombre
                                });
                            });
                        } else if (p.obtained === 1 && p.id_javamon) {
                            // Fallback for legacy pildoras where a Javamon was assigned in caza_alumno / caza_recompensa_usuari
                            const imgUrl = `${baseUrl}:8061/jutge/game/image/javamon/${p.id_javamon}`;
                            rewardItems.push({
                                key: `caza-${p.id_caza}-jm-${p.id_javamon}`,
                                visual: <img src={imgUrl} alt="javamon" style={{ width: 48, height: 48, objectFit: 'contain' }} />,
                                label: p.javamon_nom || 'Javamon',
                                pildoraName: p.nombre
                            });
                        }
                    });

                    if (!rewardItems.length) {
                        return (
                            <div style={{ textAlign: 'center', padding: '32px 0', color: '#888' }}>
                                <GiftOutlined style={{ fontSize: 36, color: '#d9d9d9', marginBottom: 12, display: 'block' }} />
                                <Text type="secondary">Aquest alumne encara no ha obtingut cap recompensa en aquest conjunt.</Text>
                            </div>
                        );
                    }
                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
                            {rewardItems.map(item => (
                                <Card
                                    key={item.key}
                                    size="small"
                                    style={{ borderRadius: 10, borderColor: '#b7eb8f', backgroundColor: '#fcffe6', width: 130, textAlign: 'center' }}
                                    bodyStyle={{ padding: 10 }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                        <div style={{ height: 48, display: 'flex', alignItems: 'center' }}>{item.visual}</div>
                                        <Text strong style={{ fontSize: 12, lineHeight: 1.2 }}>{item.label}</Text>
                                        <Text type="secondary" style={{ fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }} title={item.pildoraName}>
                                            {item.pildoraName}
                                        </Text>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    );
                })()}
            </Modal>
        </div>
    );
};

export default PildoresIdEstadistiques;
